import {
  PromptAssistanceResult,
  PromptAssistanceStreamEvent,
  type PromptAssistanceRequest,
} from "@amoreg/contracts";
import { readTextStreamLines } from "@amoreg/shared/textStream";
import * as Schema from "effect/Schema";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

export const PROMPT_ASSESSMENT_DELAY_MS = 600;

export function canAssessPrompt(prompt: string): boolean {
  const text = prompt.trim();
  return text.length >= 3 && prompt.length <= 6_000 && !text.startsWith("/");
}

export async function requestPromptAssistance(input: PromptAssistanceRequest, signal: AbortSignal) {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/prompt-assistance", {
      organizationId: input.organizationId,
    }),
    {
      method: "POST",
      credentials: "include",
      signal,
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) throw new Error("Prompt assistance unavailable");
  const result = Schema.decodeUnknownSync(PromptAssistanceResult)(await response.json());
  if (result.action !== input.action) throw new Error("Unexpected prompt response");
  return result;
}

/** A preview can grow before validation; only the final result may replace the draft. */
export async function streamPromptEnhancement(
  input: PromptAssistanceRequest,
  signal: AbortSignal,
  onProgress: (text: string) => void,
): Promise<string> {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/prompt-assistance", {
      organizationId: input.organizationId,
    }),
    {
      method: "POST",
      credentials: "include",
      signal,
      headers: { "content-type": "application/json", accept: "application/x-ndjson" },
      body: JSON.stringify({ ...input, action: "enhance" }),
    },
  );
  if (!response.ok || !response.body) throw new Error("Prompt assistance unavailable");
  // Web and server deploy independently. The previous server still returns its
  // validated JSON result while the streaming server deployment catches up.
  if (response.headers.get("content-type")?.includes("application/json")) {
    const result = Schema.decodeUnknownSync(PromptAssistanceResult)(await response.json());
    signal.throwIfAborted();
    if (result.action !== "enhance") throw new Error("Unexpected prompt response");
    return result.prompt;
  }
  if (!response.headers.get("content-type")?.includes("application/x-ndjson")) {
    await response.body.cancel();
    throw new Error("Prompt streaming unavailable");
  }
  for await (const line of readTextStreamLines(response.body, signal)) {
    if (!line.trim()) continue;
    const event = Schema.decodeUnknownSync(PromptAssistanceStreamEvent)(JSON.parse(line));
    signal.throwIfAborted();
    if (event.type === "error") throw new Error("Prompt assistance unavailable");
    if (event.type === "complete") return event.result.prompt;
    onProgress(event.text);
  }
  throw new Error("Incomplete prompt stream");
}

export function buildPromptAssistanceContext(input: {
  messages: ReadonlyArray<{ role: string; text: string }>;
  attachments: ReadonlyArray<{ name: string }>;
  selectedContext: boolean;
  selectedPassages?: ReadonlyArray<string>;
}): string {
  const context = {
    recentMessages: input.messages
      .slice(-4)
      .map(({ role, text }) => ({ role: role.slice(0, 40), text: text.slice(-1_000) })),
    attachedFiles: input.attachments.slice(0, 10).map(({ name }) => name.slice(0, 120)),
    hasSelectedContext: input.selectedContext,
    selectedPassages: (input.selectedPassages ?? [])
      .filter(Boolean)
      .slice(0, 4)
      .map((text) => text.slice(0, 800)),
  };
  // Escaped quotes/control characters can make JSON much larger than its source text.
  // Shorten fields together so even unusual drafts remain valid, bounded JSON.
  let serialized = JSON.stringify(context);
  while (serialized.length > 6_000) {
    context.recentMessages = context.recentMessages.map(({ role, text }) => ({
      role,
      text: text.slice(text.length - Math.floor(text.length / 2)),
    }));
    context.attachedFiles = context.attachedFiles.map((name) =>
      name.slice(0, Math.floor(name.length / 2)),
    );
    context.selectedPassages = context.selectedPassages.map((text) =>
      text.slice(0, Math.floor(text.length / 2)),
    );
    serialized = JSON.stringify(context);
  }
  return serialized;
}
